import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  CAvatar,
  CButton,
  CCard,
  CCardBody,
  CCardHeader,
  COffcanvas,
  COffcanvasBody,
  COffcanvasHeader,
  COffcanvasTitle,
  CBadge,
  CSpinner,
  CRow,
  CCol,
  CFormLabel,
  CFormSelect,
  CFormInput,
} from "@coreui/react";
import CIcon from "@coreui/icons-react";
import { cilPencil, cilTrash, cilX, cilZoom } from "@coreui/icons";

import { apiClient } from "services/Api";
import DataTableComponent from "components/DataTableComponent";
import ToastNotification from "components/globals/ToastNotification";
import userAvatar from "assets/images/avatars/user.png";
import SearchSelect from "components/forms/SearchSelect";
import { usePersistedState } from "components/hooks/usePersistedState";

const typologyOptions = [
  { value: "SI Inovação", label: "SI Inovação" },
  { value: "SI Internacionalização", label: "SI Internacionalização" },
  { value: "SI Qualificação", label: "SI Qualificação" },
  { value: "SI I&DT", label: "SI I&DT" },
  { value: "SI I&DT Co-Promoção", label: "SI I&DT Co-Promoção" },
  { value: "Projeto IDI", label: "Projeto IDI" },
  { value: "Outros", label: "Outros" },
];

const formatDate = (value) => {
  if (!value) return "";
  const [year, month, day] = value.split("-");
  return `${day}/${month}/${year}`;
};

// Filtros e paginação ficam guardados em sessionStorage sob este prefixo,
// para que a tabela volte ao mesmo estado quando o utilizador regressa à
// página (ex: depois de abrir/editar uma lição e voltar atrás).
const FILTERS_KEY = "learned-lessons-filters";

const LearnedLessonList = () => {
  const navigate = useNavigate();
  const [toast, setToast] = useState(null);
  const [lessons, setLessons] = useState([]);
  const [count, setCount] = useState(0);
  const [page, setPage] = usePersistedState(`${FILTERS_KEY}:page`, 1);
  const [pageSize, setPageSize] = usePersistedState(`${FILTERS_KEY}:pageSize`, 10);
  const [isLoading, setIsLoading] = useState(true);
  const [previewLesson, setPreviewLesson] = useState(null);
  const [typologyFilter, setTypologyFilter] = usePersistedState(`${FILTERS_KEY}:typology`, "");
  const [entityFilter, setEntityFilter] = usePersistedState(`${FILTERS_KEY}:entity`, null);
  const [dateFrom, setDateFrom] = usePersistedState(`${FILTERS_KEY}:dateFrom`, "");
  const [dateTo, setDateTo] = usePersistedState(`${FILTERS_KEY}:dateTo`, "");
  const [searchTerm, setSearchTerm] = usePersistedState(`${FILTERS_KEY}:search`, "");
  const [entityOptions, setEntityOptions] = useState([]);
  const [dateBounds, setDateBounds] = useState({
    min: undefined,
    max: undefined,
  });
  const debounceRef = useRef(null);
  const isFirstRun = useRef(true);
  const isFirstFilterRun = useRef(true);

  const handleNavigate = (path) => {
    setPreviewLesson(null);
    setTimeout(() => navigate(path), 0);
  };

  const fetchLessons = async () => {
    setIsLoading(true);
    try {
      const params = new URLSearchParams();
      params.set("page", String(page));
      params.set("page_size", String(pageSize));
      if (searchTerm) params.set("search", searchTerm);
      if (typologyFilter) params.set("typology", typologyFilter);
      if (entityFilter) params.set("entity", entityFilter);
      if (dateFrom) params.set("date_from", dateFrom);
      if (dateTo) params.set("date_to", dateTo);

      const response = await apiClient.get(
        `/learned-lessons/?${params.toString()}`,
      );
      setLessons(response.data.results || []);
      setCount(response.data.count || 0);
      setEntityOptions(
        (response.data.entity_options || []).map((value) => ({
          value,
          label: value,
        })),
      );
      setDateBounds({
        min: response.data.date_min || undefined,
        max: response.data.date_max || undefined,
      });
    } catch (error) {
      setToast({
        title: "Lições Apreendidas",
        message: "Erro a carregar as lições apreendidas",
        type: "error",
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Sempre que um filtro/pesquisa muda, volta à primeira página (mas não
  // logo no carregamento inicial, para não perder a página restaurada).
  useEffect(() => {
    if (isFirstFilterRun.current) {
      isFirstFilterRun.current = false;
      return;
    }
    setPage(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [typologyFilter, entityFilter, dateFrom, dateTo, searchTerm]);

  // Pedido ao servidor, com pequeno debounce para não disparar um pedido por
  // tecla premida (exceto no carregamento inicial, que é imediato).
  useEffect(() => {
    if (isFirstRun.current) {
      isFirstRun.current = false;
      fetchLessons();
      return;
    }
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      fetchLessons();
    }, 350);
    return () => clearTimeout(debounceRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, pageSize, typologyFilter, entityFilter, dateFrom, dateTo, searchTerm]);

  const handleDelete = async (lessonId) => {
    if (!window.confirm("Tem a certeza que quer eliminar este registo?")) {
      return;
    }

    try {
      await apiClient.delete(`/learned-lessons/${lessonId}/`);
      setPreviewLesson(null);
      await fetchLessons();
    } catch (error) {
      setToast({
        title: "Lições Apreendidas",
        message: "Erro ao eliminar a lição apreendida",
        type: "error",
      });
    }
  };

  useEffect(() => {
    if (
      entityFilter &&
      !entityOptions.find((opt) => opt.value === entityFilter)
    ) {
      setEntityFilter(null);
    }
  }, [entityFilter, entityOptions]);

  const columns = [
    {
      name: "Data",
      selector: (row) => row.created_at || "-",
      sortable: true,
      className: "text-center",
    },
    {
      name: "Tipologia",
      selector: (row) => row.typology || "-",
      sortable: true,
    },
    {
      name: "Entidade",
      selector: (row) => row.entity || "-",
      sortable: true,
    },
    {
      name: "Frase Resumo",
      selector: (row) => row.summary_sentence || "-",
      sortable: true,
    },
    {
      name: "Criado por",
      selector: (row) => row.created_by_name || "-",
      sortable: true,
    },
  ];

  return (
    <>
      <ToastNotification toast={toast} setToast={setToast} />

      <div className="management-filters mb-4">
        <div className="management-filters-header">
          <div className="management-section-title">Filtros</div>
          <CButton
            color="light"
            size="sm"
            onClick={() => {
              setTypologyFilter("");
              setEntityFilter(null);
              setDateFrom("");
              setDateTo("");
              setSearchTerm("");
            }}
          >
            Limpar filtros
          </CButton>
        </div>
        <CRow className="g-3 management-filters-grid">
          <CCol xs={12} md={4} lg={4}>
            <CFormLabel>Intervalo de datas</CFormLabel>
            <div className="d-flex gap-2">
              <CFormInput
                type="date"
                value={dateFrom}
                min={dateBounds.min}
                max={dateTo || dateBounds.max}
                onChange={(e) => setDateFrom(e.target.value)}
              />
              <CFormInput
                type="date"
                value={dateTo}
                min={dateFrom || dateBounds.min}
                max={dateBounds.max}
                onChange={(e) => setDateTo(e.target.value)}
              />
            </div>
          </CCol>
          <CCol xs={12} md={4} lg={4}>
            <CFormLabel>Tipologia</CFormLabel>
            <CFormSelect
              value={typologyFilter}
              onChange={(e) => setTypologyFilter(e.target.value)}
            >
              <option value="">Todas as tipologias</option>
              {typologyOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </CFormSelect>
          </CCol>
          <CCol xs={12} md={4} lg={4}>
            <CFormLabel>Entidade</CFormLabel>
            <SearchSelect
              placeholder="Filtrar por entidade"
              value={entityFilter}
              options={entityOptions}
              onChange={(value) => setEntityFilter(value)}
              clearable
            />
          </CCol>
        </CRow>
        <div className="management-active-filters">
          {[
            (dateFrom || dateTo) &&
              `Período: ${formatDate(dateFrom) || "início"} — ${
                formatDate(dateTo) || "agora"
              }`,
            typologyFilter && `Tipologia: ${typologyFilter}`,
            entityFilter && `Entidade: ${entityFilter}`,
            searchTerm && `Pesquisa: ${searchTerm}`,
          ]
            .filter(Boolean)
            .map((filter) => (
              <CBadge key={filter} color="info" className="projects-badge">
                {filter}
              </CBadge>
            ))}
          {!typologyFilter && !entityFilter && !dateFrom && !dateTo && !searchTerm ? (
            <span className="text-medium-emphasis small">
              Sem filtros aplicados.
            </span>
          ) : null}
        </div>
      </div>

      <CCard>
        <CCardHeader className="d-flex justify-content-between align-items-center">
          <h3 className="mb-0">Lições Apreendidas</h3>
          <Link to="/licoes-aprendidas/new">
            <CButton color="info" className="text-white">
              Nova Lição
            </CButton>
          </Link>
        </CCardHeader>
        <CCardBody>
          {isLoading && lessons.length === 0 ? (
            <div className="text-center">
              <CSpinner color="primary" />
            </div>
          ) : (
            <DataTableComponent
              columns={columns}
              data={lessons}
              serverSide
              totalRows={count}
              perPage={pageSize}
              page={page}
              searchValue={searchTerm}
              onSearchChange={setSearchTerm}
              onPageChange={(newPage) => setPage(newPage)}
              onRowsPerPageChange={(newPerPage, newPage) => {
                setPageSize(newPerPage);
                setPage(newPage);
              }}
              onRowClicked={(row) => setPreviewLesson(row)}
            />
          )}
        </CCardBody>
      </CCard>

      {previewLesson && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "transparent",
            zIndex: 1040,
          }}
          onClick={() => setPreviewLesson(null)}
          aria-hidden="true"
        />
      )}

      <COffcanvas
        placement="end"
        visible={Boolean(previewLesson)}
        onHide={() => setPreviewLesson(null)}
        backdrop={false}
        scrollable
        scroll
        style={{ width: "560px", maxWidth: "95vw" }}
      >
        <COffcanvasHeader className="border-bottom">
          <COffcanvasTitle className="d-flex align-items-center me-auto">
            <div>
              <div className="fw-semibold">Lição Apreendida</div>
              <div className="text-medium-emphasis small">
                #{previewLesson?.id || "-"}
              </div>
            </div>
          </COffcanvasTitle>
          <div className="d-flex align-items-center gap-2">
            {previewLesson && (
              <>
                <CButton
                  color="info"
                  variant="outline"
                  size="sm"
                  onClick={() =>
                    handleNavigate(
                      `/licoes-aprendidas/${previewLesson.id}?view=1`,
                    )
                  }
                >
                  <CIcon icon={cilZoom} className="me-1" />
                  Ver
                </CButton>
                <CButton
                  color="info"
                  size="sm"
                  className="text-white"
                  onClick={() =>
                    handleNavigate(`/licoes-aprendidas/${previewLesson.id}`)
                  }
                >
                  <CIcon icon={cilPencil} className="me-1" />
                  Editar
                </CButton>
                <CButton
                  color="danger"
                  size="sm"
                  className="text-white"
                  onClick={() => handleDelete(previewLesson.id)}
                >
                  <CIcon icon={cilTrash} className="me-1" />
                  Eliminar
                </CButton>
              </>
            )}
            <CButton
              color="link"
              className="text-body p-0"
              onClick={() => setPreviewLesson(null)}
              title="Fechar"
            >
              <CIcon icon={cilX} size="lg" />
            </CButton>
          </div>
        </COffcanvasHeader>
        <COffcanvasBody>
          {previewLesson ? (
            <div className="d-flex flex-column gap-3">
              <div className="d-flex align-items-center gap-3">
                <CAvatar
                  src={previewLesson.created_by_avatar_url || userAvatar}
                  size="lg"
                />
                <div className="flex-grow-1">
                  <div className="fw-semibold">
                    {previewLesson.created_by_name || "-"}
                  </div>
                  <div className="text-medium-emphasis small">
                    {previewLesson.created_at || "-"}
                  </div>
                </div>
                {previewLesson.typology && (
                  <CBadge color="info">{previewLesson.typology}</CBadge>
                )}
              </div>

              <div className="border rounded-3 p-3 bg-body-tertiary">
                <div className="text-uppercase text-medium-emphasis small">
                  Entidade
                </div>
                <div className="fw-semibold">{previewLesson.entity || "-"}</div>
              </div>

              <div className="border rounded-3 p-3 bg-body-tertiary">
                <div className="text-uppercase text-medium-emphasis small">
                  Frase Resumo
                </div>
                <div className="fw-semibold">
                  {previewLesson.summary_sentence || "-"}
                </div>
              </div>

              <div className="border rounded-3 p-3 bg-body-tertiary">
                <div className="text-uppercase text-medium-emphasis small">
                  Campo Formulário
                </div>
                <div className="fw-semibold">
                  {previewLesson.form_field || "-"}
                </div>
              </div>

              <div className="border rounded-3 p-3 bg-body-tertiary">
                <div className="text-uppercase text-medium-emphasis small">
                  Descrição
                </div>
                <div
                  className="mt-2"
                  dangerouslySetInnerHTML={{
                    __html: previewLesson.description || "-",
                  }}
                />
              </div>

              <div className="border rounded-3 p-3 bg-body-tertiary">
                <div className="text-uppercase text-medium-emphasis small">
                  Lições
                </div>
                <div
                  className="mt-2"
                  dangerouslySetInnerHTML={{
                    __html: previewLesson.lessons || "-",
                  }}
                />
              </div>
            </div>
          ) : (
            <div className="text-muted">
              Clique numa lição apreendida para ver o detalhe.
            </div>
          )}
        </COffcanvasBody>
      </COffcanvas>
    </>
  );
};

export default LearnedLessonList;
